// src/components/ImageUpload.jsx
import React, { useState } from 'react';
import { Upload, X, Image as ImageIcon, Loader } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../config/api';

const ImageUpload = ({ eventId, currentImages = [], onImagesUpdated }) => {
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

 
  

// Then inside component:
const API_URL = `${API_BASE_URL}/api`;

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Create preview URLs
    const previews = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(previews);
    setSelectedFiles(files);
  };

  // Upload images
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select images first');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('images', file);
      });

      const res = await axios.post(
        `${API_URL}/events/${eventId}/images`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('✅ Images uploaded:', res.data);
      alert(`✅ ${selectedFiles.length} image(s) uploaded successfully!`);
      
      // Clear selection
      setSelectedFiles([]);
      setPreviewUrls([]);
      
      // Notify parent component
      if (onImagesUpdated) {
        onImagesUpdated(res.data.event);
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      alert('❌ Failed to upload images: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  // Delete image
  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      const res = await axios.delete(
        `${API_URL}/events/${eventId}/images/${imageId}`
      );

      console.log('✅ Image deleted');
      alert('✅ Image deleted successfully!');
      
      // Notify parent component
      if (onImagesUpdated) {
        onImagesUpdated(res.data.event);
      }
    } catch (error) {
      console.error('❌ Delete error:', error);
      alert('❌ Failed to delete image: ' + (error.response?.data?.message || error.message));
    }
  };

  // Cancel selection
  const handleCancel = () => {
    setSelectedFiles([]);
    setPreviewUrls([]);
  };

  return (
    <div className="space-y-6">
      
      {/* Current Images */}
      {currentImages.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-3">Current Images ({currentImages.length})</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {currentImages.map((image) => (
              <div key={image._id} className="relative group">
                <img
                  src={image.url}
                  alt="Event"
                  className="w-full h-40 object-cover rounded-lg border-2 border-gray-200"
                />
                <button
                  onClick={() => handleDeleteImage(image._id)}
                  className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  title="Delete"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Section */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
        <div className="text-center">
          <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-2">Upload Event Images</h3>
          <p className="text-sm text-gray-600 mb-4">
            Select up to 10 images (JPEG, PNG, GIF, WebP - Max 5MB each)
          </p>

          <input
            type="file"
            id="image-upload"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />

          <label
            htmlFor="image-upload"
            className="inline-block px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-bold cursor-pointer hover:from-blue-600 hover:to-blue-700 transition-all"
          >
            <Upload className="w-5 h-5 inline-block mr-2" />
            Select Images
          </label>
        </div>
      </div>

      {/* Preview Selected Images */}
      {previewUrls.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-3">
            Selected Images ({previewUrls.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
            {previewUrls.map((url, index) => (
              <div key={index} className="relative">
                <img
                  src={url}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-40 object-cover rounded-lg border-2 border-blue-500"
                />
              </div>
            ))}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-bold hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-bold hover:from-green-600 hover:to-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Upload {selectedFiles.length} Image(s)
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
